/**
 * Top filters for narrowing analysis results
 */

export type StatusFilter = 'all' | 'active' | 'inactive' | 'error';
export type MatchFilter = 'all' | '50' | '80';

export interface AnalysisFilters {
  query: string;
  steemit: StatusFilter;
  blurt: StatusFilter;
  minMatch: MatchFilter;
  hideMuted: boolean;
}

interface FilterBarProps {
  filters: AnalysisFilters;
  onChange: (next: AnalysisFilters) => void;
  onReset: () => void;
  totalCount: number;
  filteredCount: number;
}

export function FilterBar({
  filters,
  onChange,
  onReset,
  totalCount,
  filteredCount,
}: FilterBarProps) {
  return (
    <div className="bg-white rounded-lg shadow-md border border-slate-200 p-4 mb-6">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-60 flex-1">
          <label htmlFor="filter-query" className="block text-xs font-semibold text-slate-700 mb-1">
            Search
          </label>
          <input
            id="filter-query"
            type="text"
            value={filters.query}
            onChange={(e) => onChange({ ...filters, query: e.target.value })}
            placeholder="Author, title, or permlink"
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-hive"
          />
        </div>

        <div>
          <label htmlFor="filter-steemit" className="block text-xs font-semibold text-slate-700 mb-1">
            Steemit
          </label>
          <select
            id="filter-steemit"
            value={filters.steemit}
            onChange={(e) => onChange({ ...filters, steemit: e.target.value as StatusFilter })}
            className="px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="error">Error</option>
          </select>
        </div>

        <div>
          <label htmlFor="filter-blurt" className="block text-xs font-semibold text-slate-700 mb-1">
            Blurt
          </label>
          <select
            id="filter-blurt"
            value={filters.blurt}
            onChange={(e) => onChange({ ...filters, blurt: e.target.value as StatusFilter })}
            className="px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="error">Error</option>
          </select>
        </div>

        <div>
          <label htmlFor="filter-match" className="block text-xs font-semibold text-slate-700 mb-1">
            Match Score
          </label>
          <select
            id="filter-match"
            value={filters.minMatch}
            onChange={(e) => onChange({ ...filters, minMatch: e.target.value as MatchFilter })}
            className="px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
          >
            <option value="all">Any</option>
            <option value="50">50%+</option>
            <option value="80">80%+</option>
          </select>
        </div>

        <button
          type="button"
          onClick={onReset}
          className="px-3 py-2 border border-slate-300 rounded-md text-sm text-slate-700 hover:bg-slate-50"
        >
          Reset
        </button>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <input
          id="filter-hide-muted"
          type="checkbox"
          checked={filters.hideMuted}
          onChange={(e) => onChange({ ...filters, hideMuted: e.target.checked })}
          className="h-4 w-4 rounded border-slate-300 text-hive focus:ring-red-200"
        />
        <label htmlFor="filter-hide-muted" className="text-xs text-slate-700">
          Hide already muted accounts
        </label>
      </div>

      <p className="mt-3 text-xs text-slate-600">
        Showing <span className="font-semibold text-slate-800">{filteredCount}</span> of{' '}
        <span className="font-semibold text-slate-800">{totalCount}</span> posts
      </p>
    </div>
  );
}
